import React, { useEffect, useState } from 'react';
import {
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
} from 'reactstrap';
import axios from 'axios';
import notify from '../../../utils/notify';
import logger from '../../../utils/logger';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import { formatNumber } from '../../../utils/numberformatter';
import ActionButton from '../../../Components/Common/ActionButton';
import { EmptyState } from '../../../Components/Common/ListStates';
import { SkeletonTable } from '../../../Components/Common/Skeleton';
import { FormField, RequiredNote, SelectField } from '../../../Components/Form';

const showNumber = value =>
  value === null || value === undefined ? '-' : formatNumber(value);

/**
 * Modal untuk memilih jetty Port Restriction procurement dari master
 * (konsep sama dengan Select Master Document). Location dibatasi ke port
 * Delivery Term procurement ini; jetty yang dicentang disimpan per procurement
 * lewat POST /procurement-port-restriction/select-jetty dan nilai DWT/LOA/Draft
 * tersalin dari master. Jetty yang sudah dipilih tidak ditampilkan lagi.
 */
const AddPortModal = ({
  isOpen,
  toggle,
  locationOptions = [],
  procurementId,
  selectedJettyIds = [],
  onSaved,
}) => {
  const [locationId, setLocationId] = useState('');
  const [jetties, setJetties] = useState([]);
  const [loadingJetties, setLoadingJetties] = useState(false);
  const [checkedIds, setCheckedIds] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!locationId) {
      setJetties([]);
      return undefined;
    }

    let cancelled = false;
    const token = getAuthToken();
    (async () => {
      setLoadingJetties(true);
      try {
        const params = new URLSearchParams({
          LocationIds: locationId,
          IsActive: 'true',
          PageSize: '1000',
        });
        const response = await axios.get(
          `${api.API_URL}/location-jetty/list?${params.toString()}`,
          {
            withXSRFToken: true,
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const result = response?.data ?? response;
        const items = result?.data?.items || result?.items || [];
        if (!cancelled) {
          setJetties(Array.isArray(items) ? items : []);
        }
      } catch (error) {
        if (!cancelled) {
          logger.error('Error fetching location jetty:', error);
          setJetties([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingJetties(false);
        }
      }
    })();

    // Cegah response location lama menimpa location yang baru dipilih.
    return () => {
      cancelled = true;
    };
  }, [locationId]);

  const availableJetties = jetties
    .filter(jetty => !selectedJettyIds.includes(jetty.id))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const allChecked =
    availableJetties.length > 0 &&
    checkedIds.length === availableJetties.length;

  const reset = () => {
    setLocationId('');
    setJetties([]);
    setCheckedIds([]);
    setErrors({});
  };

  const handleClose = () => {
    if (saving) {
      return;
    }
    reset();
    toggle();
  };

  const handleLocationChange = value => {
    setLocationId(value ?? '');
    setCheckedIds([]);
    setErrors({});
  };

  const handleToggleJetty = id => {
    setCheckedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
    setErrors(prev => ({ ...prev, jetties: '' }));
  };

  const handleToggleAll = () => {
    setCheckedIds(allChecked ? [] : availableJetties.map(jetty => jetty.id));
    setErrors(prev => ({ ...prev, jetties: '' }));
  };

  const handleSave = async () => {
    const nextErrors = {
      locationId: locationId ? '' : 'Location is required.',
      jetties:
        locationId && checkedIds.length === 0
          ? 'Select at least one jetty.'
          : '',
    };
    setErrors(nextErrors);
    if (nextErrors.locationId || nextErrors.jetties) {
      return;
    }

    const token = getAuthToken();
    setSaving(true);
    try {
      await axios.post(
        `${api.API_URL}/procurement-port-restriction/select-jetty`,
        { procurementId, locationJettyIds: checkedIds },
        {
          withXSRFToken: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      notify.success(
        `${checkedIds.length} jetty(s) added to port restriction.`
      );
      reset();
      toggle();
      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      notify.apiError(error, 'add the port');
    } finally {
      setSaving(false);
    }
  };

  const renderJetties = () => {
    if (!locationId) {
      return (
        <p className='text-muted fs-13 mb-0'>
          Select a location to see its jetties.
        </p>
      );
    }
    if (loadingJetties) {
      return <SkeletonTable rows={3} columns={8} />;
    }
    if (availableJetties.length === 0) {
      return (
        <EmptyState
          compact
          icon='ri-anchor-line'
          title='No jetty available'
          emptyHint={
            jetties.length === 0
              ? 'No jetty for this location. Add jetties in Master Data → Data Port Restriction'
              : 'Every jetty of this location has already been added.'
          }
        />
      );
    }
    return (
      <div
        className='table-responsive'
        style={{ maxHeight: '300px', overflowY: 'auto' }}
      >
        <Table className='align-middle table-nowrap mb-0' size='sm'>
          <thead className='table-light'>
            <tr>
              <th
                rowSpan={2}
                className='text-center align-middle'
                style={{ width: '50px' }}
              >
                <div className='form-check'>
                  <Input
                    className='form-check-input'
                    type='checkbox'
                    aria-label='Select all jetties'
                    checked={allChecked}
                    // reactstrap Input: `ref` = instance komponen, DOM lewat innerRef.
                    innerRef={el => {
                      if (el)
                        el.indeterminate =
                          checkedIds.length > 0 &&
                          checkedIds.length < availableJetties.length;
                    }}
                    onChange={handleToggleAll}
                  />
                </div>
              </th>
              <th rowSpan={2} className='align-middle'>
                Jenis Sarana Tambat
              </th>
              <th colSpan={2} className='text-center'>
                DWT (Tons)
              </th>
              <th colSpan={2} className='text-center'>
                LOA (M)
              </th>
              <th colSpan={2} className='text-center'>
                Draft (M)
              </th>
            </tr>
            <tr>
              <th className='text-center'>Min</th>
              <th className='text-center'>Max</th>
              <th className='text-center'>Min</th>
              <th className='text-center'>Max</th>
              <th className='text-center'>Min</th>
              <th className='text-center'>Max</th>
            </tr>
          </thead>
          <tbody>
            {availableJetties.map(jetty => (
              <tr key={jetty.id}>
                <td className='text-center'>
                  <div className='form-check'>
                    <Input
                      className='form-check-input'
                      type='checkbox'
                      aria-label={`Select ${jetty.name}`}
                      checked={checkedIds.includes(jetty.id)}
                      onChange={() => handleToggleJetty(jetty.id)}
                    />
                  </div>
                </td>
                <td>{jetty.name || '-'}</td>
                <td className='text-center'>{showNumber(jetty.dwtMin)}</td>
                <td className='text-center'>{showNumber(jetty.dwtMax)}</td>
                <td className='text-center'>{showNumber(jetty.loaMin)}</td>
                <td className='text-center'>{showNumber(jetty.loaMax)}</td>
                <td className='text-center'>{showNumber(jetty.draftMin)}</td>
                <td className='text-center'>{showNumber(jetty.draftMax)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size='lg'>
      <ModalHeader toggle={handleClose} className='fw-semibold'>
        Add Port
      </ModalHeader>
      <ModalBody>
        <RequiredNote />
        <SelectField
          label='Location'
          name='locationId'
          required
          placeholder='Select location'
          options={locationOptions}
          value={locationId}
          error={errors.locationId}
          onChange={handleLocationChange}
        />
        <FormField
          group
          label='Jenis Sarana Tambat'
          name='jetties'
          required
          error={errors.jetties}
        >
          {renderJetties()}
        </FormField>
      </ModalBody>

      <ModalFooter>
        <ActionButton
          variant='secondary'
          size='sm'
          icon='ri-close-line'
          disabled={saving}
          onClick={handleClose}
        >
          Close
        </ActionButton>
        <ActionButton
          variant='primary'
          size='sm'
          icon='ri-add-line'
          loading={saving}
          disabled={checkedIds.length === 0}
          onClick={handleSave}
        >
          {checkedIds.length > 0
            ? `Add Port (${checkedIds.length})`
            : 'Add Port'}
        </ActionButton>
      </ModalFooter>
    </Modal>
  );
};

export default AddPortModal;
